import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BrandLockup } from "@/components/BrandMark";
import { ArrowLeft, Shield, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ExchangePolicy() {
  const [policy, setPolicy] = useState(null);

  useEffect(() => {
    fetch(`${API}/exchange-policy`)
      .then((r) => r.json())
      .then(setPolicy)
      .catch(console.error);
  }, []);

  if (!policy) return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "var(--mp-paper)" }}><p>Loading...</p></div>;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--mp-paper)" }} data-testid="exchange-policy-page">
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-[var(--mp-line)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-16 lg:h-20">
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="policy-back-btn">
              <Button variant="ghost" className="p-2 rounded-sm">
                <ArrowLeft className="w-5 h-5" style={{ color: "var(--mp-text-strong)" }} />
              </Button>
            </Link>
            <h1 className="text-lg font-medium" style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}>
              Exchange Policy
            </h1>
          </div>
          <Link to="/">
            <span className="text-sm font-medium" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text-strong)" }}><BrandLockup /></span>
          </Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-10 lg:py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center pastel-peach">
            <Shield className="w-8 h-8" style={{ color: "var(--mp-copper)" }} />
          </div>
          <h1 className="text-3xl md:text-4xl font-medium mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}>
            {policy.title}
          </h1>
          <p className="text-sm" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}>
            Effective since {policy.effective_date}
          </p>
        </div>

        {/* Summary box */}
        <div className="pastel-yellow border border-[var(--mp-line)] rounded-sm p-6 mb-10 flex items-start gap-4" data-testid="policy-summary">
          <AlertTriangle className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: "var(--mp-copper-deep)" }} />
          <div>
            <p className="text-sm font-semibold mb-1" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text)" }}>Important</p>
            <p className="text-sm" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}>{policy.summary}</p>
          </div>
        </div>

        {/* Terms */}
        <div className="space-y-5">
          {policy.terms.map((term, idx) => (
            <div key={term.title || term.heading || idx} className="bg-white border border-[var(--mp-line)] rounded-sm p-6" data-testid={`policy-term-${idx}`}>
              <div className="flex items-start gap-4">
                <span className="text-xs font-bold w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "var(--mp-solid)", color: "white", fontFamily: "'Manrope', sans-serif" }}>
                  {idx + 1}
                </span>
                <div>
                  <h3 className="text-base font-semibold mb-2" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text)" }}>
                    {term.title}
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}>
                    {term.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Contact */}
        <div className="mt-10 pastel-green border border-[var(--mp-line)] rounded-sm p-6 text-center">
          <p className="text-sm mb-3" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}>
            For exchange requests or queries, contact us on
          </p>
          <a href="https://wa.me/917624853840" target="_blank" rel="noopener noreferrer">
            <Button className="text-white text-sm px-6 py-3 rounded-sm" style={{ backgroundColor: "#25D366" }} data-testid="policy-whatsapp-btn">
              WhatsApp: +91 7624853840
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
}
